import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useWindowSize } from '../hooks/useWindowSize';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Toast from '../components/Toast';
import * as XLSX from 'xlsx';
import { API } from '../api';
import { C, E, RAYON, T, champ as champStyle, bouton } from '../theme';
import { SqueletteTableau } from '../components/Squelette';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import EtatVide from '../components/EtatVide';
import { TYPE_CONTRAT_LABELS } from './Assurance';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

const GEOCODE_CACHE_KEY = 'crm_geocode_cache_v1';

// Formulaire vierge de la fenêtre « Nouvelle affaire chaude ». `module` décide
// de ce qui est créé derrière l'entreprise : un projet de financement (crédit)
// ou un contrat d'assurance. Les deux vivent dans des tables distinctes et
// alimentent chacun les affaires chaudes de leur module sur le tableau de
// bord — rien ne passe de l'un à l'autre.
const AC_FORM_VIDE = {
  module: 'credit',
  // Véhicule : une affaire qui porte les deux métiers à la fois — le
  // financement du véhicule et son assurance. On peut n'en garder qu'un.
  designation: '', avec_financement: true, avec_assurance: true,
  nom_societe: '', siren: '', nom_dirigeant: '', adresse: '', date_echeance: '',
  valeur_bien: '',                              // crédit
  type_contrat: 'flotte', prime_annuelle: '',   // assurance
};
function loadCache() {
  try { return JSON.parse(localStorage.getItem(GEOCODE_CACHE_KEY) || '{}'); }
  catch { return {}; }
}
function saveCache(cache) {
  try { localStorage.setItem(GEOCODE_CACHE_KEY, JSON.stringify(cache)); }
  catch {}
}
async function geocodeAddress(address, cache) {
  const key = address.trim().toLowerCase();
  if (cache[key] !== undefined) return cache[key];
  try {
    const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1&countrycodes=fr`;
    const res = await fetch(url, { headers: { 'Accept-Language': 'fr', 'User-Agent': 'CRM-CB-Courtage/1.0' } });
    const data = await res.json();
    const coords = data.length > 0 ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) } : null;
    cache[key] = coords;
    return coords;
  } catch { return null; }
}

const TYPE_CONFIG_MAP = {
  prospect: { color: '#f97316', label: 'Prospect' },
  client:   { color: '#22c55e', label: 'Client' },
  apporteur:{ color: '#6366f1', label: 'Apporteur' },
};
const STATUT_COLORS_MAP = {
  'En attente': '#f59e0b', 'Validé': '#10b981', 'Sans suite': '#9ca3af',
  'Perdu': '#ef4444', 'Perdu mais à relancer': '#f97316', "À l'étude": '#3b82f6',
  'Gagné': '#059669', 'Gagné facturé': '#0891b2', 'Signé': '#7c3aed',
};

function createPinIcon(color) {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="36" viewBox="0 0 28 36"><path d="M14 0C6.268 0 0 6.268 0 14c0 9.333 14 22 14 22S28 23.333 28 14C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="1.5"/><circle cx="14" cy="14" r="5" fill="white"/></svg>`;
  return L.divIcon({ className: '', html: svg, iconSize: [28, 36], iconAnchor: [14, 36], popupAnchor: [0, -36] });
}

function ClusterLayer({ markers, navigate }) {
  const map = useMap();
  useEffect(() => {
    const cluster = L.markerClusterGroup({ chunkedLoading: true });
    markers.forEach(p => {
      const cfg = TYPE_CONFIG_MAP[p.type] || TYPE_CONFIG_MAP.prospect;
      const sColor = STATUT_COLORS_MAP[p.statut] || '#6b7280';
      const marker = L.marker([p.coords.lat, p.coords.lng], { icon: createPinIcon(cfg.color) });
      marker.bindPopup(`
        <div style="min-width:210px;font-family:'Segoe UI',Arial,sans-serif">
          <div style="display:flex;justify-content:space-between;margin-bottom:8px">
            <span style="background:${cfg.color};color:white;font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px">${cfg.label}</span>
            <span style="background:${sColor}22;color:${sColor};font-size:11px;font-weight:600;padding:2px 8px;border-radius:10px;border:1px solid ${sColor}44">${p.statut}</span>
          </div>
          <div style="font-weight:700;font-size:15px;color:#1a2b4a;margin-bottom:6px">${p.nom_societe}</div>
          ${p.nom_dirigeant ? `<div style="font-size:13px;color:#4b5563;margin-bottom:3px">${p.nom_dirigeant}</div>` : ''}
          <button data-pid="${p.id}" style="margin-top:10px;background:#1a2b4a;color:white;border:none;border-radius:6px;padding:8px;font-size:12px;font-weight:600;cursor:pointer;width:100%">
            Voir la fiche
          </button>
        </div>
      `);
      marker.on('popupopen', () => {
        const btn = document.querySelector(`button[data-pid="${p.id}"]`);
        if (btn) btn.onclick = () => navigate(`/prospects/${p.id}`);
      });
      cluster.addLayer(marker);
    });
    map.addLayer(cluster);
    if (markers.length > 0) {
      try {
        map.fitBounds(L.latLngBounds(markers.map(m => [m.coords.lat, m.coords.lng])), { padding: [40, 40], maxZoom: 13 });
      } catch {}
    }
    return () => { map.removeLayer(cluster); };
  }, [markers, map, navigate]);
  return null;
}


function Prospects() {
  const { isMobile, isTablet } = useWindowSize();
  const gridCols = isMobile || isTablet ? '1fr' : '1fr 1fr';
  const [prospects, setProspects] = useState([]);
  // Le tableau affichait « 0 entreprise(s) trouvée(s) » le temps du
  // chargement, ce qui se lit comme un résultat, pas comme une attente.
  const [chargement, setChargement] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [recherche, setRecherche] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [filtreType, setFiltreType] = useState('Tous');
  const [form, setForm] = useState({
    nom_societe: '', siren: '', nom_dirigeant: '',
    adresse: '', telephone: '', email: '', canal: '', type: 'prospect'
  });
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [urlFilterIds, setUrlFilterIds] = useState(null);
  const [toast, setToast] = useState(null);
  const searchRef = useRef(null);
  const suggestionsRef = useRef(null);
  const [page, setPage] = useState(1);

  // ── Modal "Nouvelle affaire chaude" ──
  const [showAcModal, setShowAcModal] = useState(false);
  const [acSearchQuery, setAcSearchQuery] = useState('');
  const [acSuggestions, setAcSuggestions] = useState([]);
  const [acShowSuggestions, setAcShowSuggestions] = useState(false);
  const [acSearchLoading, setAcSearchLoading] = useState(false);
  const [acForm, setAcForm] = useState(AC_FORM_VIDE);
  const [acSubmitting, setAcSubmitting] = useState(false);
  const acSearchRef = useRef(null);
  const parPage = 20;
  const [tri, setTri] = useState({ colonne: 'nom_societe', direction: 'asc' });
  const [vueMode, setVueMode] = useState('tableau');
  const [allGeoMarkers, setAllGeoMarkers] = useState([]);
  const [geocoding, setGeocoding] = useState(false);
  const [geocodeProgress, setGeocodeProgress] = useState(0);
  const [geocodeTotal, setGeocodeTotal] = useState(0);
  const abortGeoRef = useRef(false);

  const COLONNES = [
    { label: 'Société',   champ: 'nom_societe' },
    { label: 'Dirigeant', champ: 'nom_dirigeant' },
    { label: 'Téléphone', champ: 'telephone' },
    { label: 'Email',     champ: 'email' },
    { label: 'Type',      champ: 'type' },
  ];

  const toggleTri = (champ) => {
    setTri(t => t.colonne === champ
      ? { colonne: champ, direction: t.direction === 'asc' ? 'desc' : 'asc' }
      : { colonne: champ, direction: 'asc' }
    );
    setPage(1);
  };

  const geocodeAll = async (currentProspects) => {
    const withAddr = currentProspects.filter(p => p.adresse && p.adresse.trim());
    if (!withAddr.length) return;
    setGeocoding(true);
    setGeocodeTotal(withAddr.length);
    setGeocodeProgress(0);
    abortGeoRef.current = false;
    const cache = loadCache();
    const results = [];
    for (let i = 0; i < withAddr.length; i++) {
      if (abortGeoRef.current) break;
      const p = withAddr[i];
      setGeocodeProgress(i + 1);
      const key = p.adresse.trim().toLowerCase();
      if (cache[key] === undefined && i > 0) await new Promise(r => setTimeout(r, 1100));
      const coords = await geocodeAddress(p.adresse, cache);
      if (coords) results.push({ ...p, coords });
    }
    saveCache(cache);
    setAllGeoMarkers(results);
    setGeocoding(false);
  };

  useEffect(() => {
    axios.get(`${API}/prospects`)
      .then(res => setProspects(res.data))
      .catch(() => {})
      .finally(() => setChargement(false));
  }, []);

  useEffect(() => {
    const ids = searchParams.get('ids');
    if (ids) {
      setUrlFilterIds(ids.split(',').map(Number).filter(Boolean));
    }
    // Le tableau de bord envoie ici pour créer une affaire chaude dans un
    // métier donné : la fenêtre s'ouvre seule, sur le bon module. Sans ça,
    // arriver sur la liste des entreprises après avoir cliqué « Assurance —
    // 0 » laisse chercher le bouton.
    const module = searchParams.get('nouvelle_affaire');
    if (module === 'credit' || module === 'assurance' || module === 'vehicule') {
      setAcForm({ ...AC_FORM_VIDE, module });
      setAcSearchQuery('');
      setShowAcModal(true);
    }
  }, [searchParams]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (searchQuery.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    const timer = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const response = await axios.get(
          `https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(searchQuery)}&page=1&per_page=8`
        );
        if (response.data.results) {
          setSuggestions(response.data.results);
          setShowSuggestions(true);
        }
      } catch (e) {
        setSuggestions([]);
      }
      setSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const selectEntreprise = (entreprise) => {
    const dirigeant = entreprise.dirigeants?.[0];
    const nomDirigeant = dirigeant ? `${dirigeant.prenom || ''} ${dirigeant.nom || ''}`.trim() : '';
    const adresse = entreprise.siege?.adresse || '';
    setForm({
      ...form,
      nom_societe: entreprise.nom_raison_sociale || '',
      siren: entreprise.siren || '',
      nom_dirigeant: nomDirigeant,
      adresse: adresse,
    });
    setSearchQuery(entreprise.nom_raison_sociale || '');
    setShowSuggestions(false);
  };

  const handleSubmit = () => {
    const champsObligatoires = ['nom_societe', 'siren', 'nom_dirigeant', 'telephone', 'email'];
    const manquants = champsObligatoires.filter(c => !form[c] || form[c].trim() === '');
    if (manquants.length > 0) {
      alert(`Champs obligatoires manquants : ${manquants.join(', ')}`);
      return;
    }
    axios.post(`${API}/prospects`, form)
      .then(res => {
        setProspects([...prospects, res.data]);
        setShowForm(false);
        setForm({ nom_societe: '', siren: '', nom_dirigeant: '', adresse: '', telephone: '', email: '', statut: 'En attente', canal: '', type: 'prospect' });
        setSearchQuery('');
        setToast({ message: 'Prospect créé avec succès !', type: 'success' });
      });
  };

  // ── Affaire chaude modal : SIREN autocomplete ──
  useEffect(() => {
    if (!showAcModal) return;
    if (acSearchQuery.length < 2) { setAcSuggestions([]); setAcShowSuggestions(false); return; }
    const timer = setTimeout(async () => {
      setAcSearchLoading(true);
      try {
        const res = await axios.get(`https://recherche-entreprises.api.gouv.fr/search?q=${encodeURIComponent(acSearchQuery)}&page=1&per_page=8`);
        if (res.data.results) { setAcSuggestions(res.data.results); setAcShowSuggestions(true); }
      } catch { setAcSuggestions([]); }
      setAcSearchLoading(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [acSearchQuery, showAcModal]);

  const acSelectEntreprise = (e) => {
    const dirigeant = e.dirigeants?.[0];
    const nomDirigeant = dirigeant ? `${dirigeant.prenom || ''} ${dirigeant.nom || ''}`.trim() : '';
    setAcForm(f => ({ ...f, nom_societe: e.nom_raison_sociale || '', siren: e.siren || '', nom_dirigeant: nomDirigeant, adresse: e.siege?.adresse || '' }));
    setAcSearchQuery(e.nom_raison_sociale || '');
    setAcShowSuggestions(false);
  };

  const handleAffaireChaudeSubmit = async () => {
    if (!acForm.nom_societe) return alert('Le nom de la société est obligatoire');
    if (!acForm.date_echeance) return alert("La date d'échéance est obligatoire");
    const vehicule = acForm.module === 'vehicule';
    // Véhicule : financement et/ou assurance, au choix. Au moins un des deux,
    // sans quoi l'affaire ne créerait rien et le dirait pourtant réussie.
    if (vehicule && !acForm.avec_financement && !acForm.avec_assurance) {
      return alert('Cochez au moins le financement ou l\'assurance.');
    }
    if (vehicule && !acForm.designation.trim()) {
      return alert('Désignez le véhicule (marque, modèle…).');
    }
    const assurance = acForm.module === 'assurance' || (vehicule && acForm.avec_assurance);
    const credit = acForm.module === 'credit' || (vehicule && acForm.avec_financement);
    setAcSubmitting(true);
    try {
      const prospectRes = await axios.post(`${API}/prospects`, {
        nom_societe: acForm.nom_societe, siren: acForm.siren || '',
        nom_dirigeant: acForm.nom_dirigeant || '', adresse: acForm.adresse || '',
        telephone: '', email: '', canal: '', type: 'prospect'
      });
      const newProspect = prospectRes.data;

      if (assurance) {
        // Statut « En attente » : c'est ce que le tableau de bord compte comme
        // affaire chaude de l'assurance. Numéro de contrat, compagnie et
        // fractionnement se renseignent plus tard, dans la fiche entreprise —
        // à ce stade on ne les connaît pas encore.
        await axios.post(`${API}/contrats-assurance`, {
          prospect_id: newProspect.id,
          type_contrat: vehicule ? 'vehicule' : acForm.type_contrat,
          prime_annuelle: parseFloat(acForm.prime_annuelle) || null,
          date_echeance: acForm.date_echeance,
          fractionnement_paiement: 1,
          statut: 'En attente',
        });
      }
      if (credit) {
        const montant = parseFloat(acForm.valeur_bien) || null;
        // Pour un véhicule, c'est lui qu'on finance : son nom est celui du
        // matériel, et non le nom de la société.
        const libelle = vehicule ? acForm.designation.trim() : acForm.nom_societe;
        await axios.post(`${API}/projets`, {
          prospect_id: newProspect.id,
          nom_produit: libelle,
          materiel: libelle,
          valeur_ht: montant || 0,
          montant_financement: montant,
          date_echeance: acForm.date_echeance,
          valeur_residuelle: 1,
          retribution_apporteur: 0,
          frais_dossier: 0,
          premier_loyer_plm: 0,
        });
      }

      setProspects(prev => [...prev, newProspect]);
      setUrlFilterIds(prev => prev ? [...prev, newProspect.id] : [newProspect.id]);
      setShowAcModal(false);
      setAcForm(AC_FORM_VIDE);
      setAcSearchQuery('');
      setToast({
        message: vehicule
          ? `🔥 Affaire véhicule créée (${[credit && 'financement', assurance && 'assurance'].filter(Boolean).join(' et ')}) !`
          : assurance
            ? '🔥 Affaire chaude assurance créée avec succès !'
            : '🔥 Affaire chaude crédit créée avec succès !',
        type: 'success',
      });
    } catch {
      alert('Erreur lors de la création. Veuillez réessayer.');
    }
    setAcSubmitting(false);
  };

  const TYPE_CONFIG = { prospect: { color: '#f97316', label: 'Prospect' }, client: { color: '#059669', label: 'Client' }, apporteur: { color: '#6366f1', label: 'Apporteur' } };
  const getTypeColor = (type) => (TYPE_CONFIG[type] || TYPE_CONFIG.prospect).color;
  const getTypeLabel = (type) => (TYPE_CONFIG[type] || TYPE_CONFIG.prospect).label;

  const prospectsFiltres = prospects.filter(p => {
    const matchRecherche = p.nom_societe?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.nom_dirigeant?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.email?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.siren?.includes(recherche);
    const matchType = filtreType === 'Tous' || (p.type || 'prospect') === filtreType;
    const matchIds = !urlFilterIds || urlFilterIds.includes(p.id);
    return matchRecherche && matchType && matchIds;
  });
  const prospectsTries = tri.colonne
    ? [...prospectsFiltres].sort((a, b) => {
        const va = (a[tri.colonne] || '').toString().toLowerCase();
        const vb = (b[tri.colonne] || '').toString().toLowerCase();
        return tri.direction === 'asc' ? va.localeCompare(vb, 'fr') : vb.localeCompare(va, 'fr');
      })
    : prospectsFiltres;

  const totalPages = Math.ceil(prospectsTries.length / parPage);
  const prospectsAffiches = prospectsTries.slice((page - 1) * parPage, page * parPage);

  const displayedGeoMarkers = allGeoMarkers.filter(p => {
    const matchRecherche = !recherche ||
      p.nom_societe?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.nom_dirigeant?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.email?.toLowerCase().includes(recherche.toLowerCase()) ||
      p.siren?.includes(recherche);
    const matchType = filtreType === 'Tous' || (p.type || 'prospect') === filtreType;
    const matchIds = !urlFilterIds || urlFilterIds.includes(p.id);
    return matchRecherche && matchType && matchIds;
  });

const exportExcel = () => {
  const data = prospectsFiltres.map(p => ({
    'Type': getTypeLabel(p.type || 'prospect'),
    'Société': p.nom_societe,
    'Dirigeant': p.nom_dirigeant,
    'SIREN': p.siren,
    'Téléphone': p.telephone,
    'Email': p.email,
    'Adresse': p.adresse,
    'Canal': p.canal,
  }));
  const ws = XLSX.utils.json_to_sheet(data);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Prospects');
  XLSX.writeFile(wb, 'prospects_cb_courtage.xlsx');
  setToast({ message: 'Export Excel réussi !', type: 'success' });
};

  const inputStyle = champStyle;

  const champObligatoire = (key) => ['nom_societe', 'siren', 'nom_dirigeant', 'telephone', 'email'].includes(key);

  const formatPhone = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 10);
    return digits.match(/.{1,2}/g)?.join(' ') || '';
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end',
                    gap: E.lg, flexWrap: 'wrap', marginBottom: E.lg }}>
        <div>
          <h1 style={T.titre}>Entreprises</h1>
          <p style={{ ...T.sousTitre, marginTop: '2px' }}>
            {chargement ? 'Chargement…' : `${prospectsFiltres.length} entreprise(s)${recherche || filtreType !== 'Tous' ? ' correspondant au filtre' : ' au portefeuille'}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          {urlFilterIds && (
            <button onClick={() => { setAcForm(AC_FORM_VIDE); setAcSearchQuery(''); setShowAcModal(true); }}
              style={{ ...bouton('principal'), background: C.danger, borderColor: C.danger }}>
              Nouvelle affaire chaude
            </button>
          )}
          <button onClick={() => setShowForm(!showForm)} style={bouton('principal')}>
            + Nouvelle entreprise
          </button>
        </div>
      </div>
      {/* Une seule barre d'outils : la recherche, le filtre et les deux vues
          étaient répartis sur deux lignes, ce qui repoussait le tableau vers
          le bas sans rien apporter. */}
      <div style={{
        display: 'flex', gap: E.sm, marginBottom: E.lg, flexWrap: 'wrap',
        alignItems: 'center', background: C.surface, padding: E.md,
        border: `1px solid ${C.bordure}`, borderRadius: RAYON.lg,
      }}>
        <input placeholder="Rechercher une société, un dirigeant, un email, un SIREN…"
          value={recherche} onChange={e => setRecherche(e.target.value)}
          style={{ ...inputStyle, flex: '1 1 260px', width: 'auto', minWidth: 0 }} />
        <select value={filtreType} onChange={e => setFiltreType(e.target.value)}
          style={{ ...inputStyle, width: 'auto', flex: '0 0 auto' }}>
          <option value="Tous">Tous types</option>
          <option value="prospect">Prospect</option>
          <option value="client">Client</option>
          <option value="apporteur">Apporteur</option>
        </select>
        <div style={{ display: 'flex', gap: E.sm, marginLeft: isMobile ? 0 : 'auto' }}>
          <button onClick={exportExcel} style={bouton('secondaire')}>Export Excel</button>
          <button
            onClick={() => {
              const next = vueMode === 'carte' ? 'tableau' : 'carte';
              setVueMode(next);
              if (next === 'carte' && !geocoding && allGeoMarkers.length === 0) {
                geocodeAll(prospects);
              }
            }}
            style={vueMode === 'carte' ? bouton('principal') : bouton('secondaire')}>
            {vueMode === 'carte' ? 'Vue tableau' : 'Vue carte'}
          </button>
        </div>
      </div>

      {showForm && (
        <div style={{ background: 'white', padding: '24px', borderRadius: '12px', marginBottom: '24px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)', border: '1px solid #e5e7eb' }}>
          <h2 style={{ color: '#1a2b4a', fontSize: '16px', fontWeight: '600', margin: '0 0 20px' }}>Nouveau prospect / client</h2>

          <div style={{ background: '#f0f7ff', padding: '16px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #bfdbfe' }} ref={searchRef}>
            <p style={{ margin: '0 0 10px', fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>Recherche automatique (nom ou SIREN)</p>
            <div style={{ position: 'relative' }}>
              <input
                placeholder="Tapez le nom ou le SIREN de l'entreprise..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                style={{ ...inputStyle, paddingRight: '40px' }}
              />
              {searchLoading && (
                <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '12px' }}>
                  ...
                </span>
              )}
              {showSuggestions && suggestions.length > 0 && (
                <div ref={suggestionsRef} style={{
                  position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
                  background: 'white', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  border: '1px solid #e5e7eb', maxHeight: '300px', overflowY: 'auto', marginTop: '4px'
                }}>
                  {suggestions.map((s, i) => (
                    <div key={i} onClick={() => selectEntreprise(s)}
                      style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                      onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                      onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                      <div style={{ fontWeight: '600', color: '#1a2b4a', fontSize: '13px' }}>{s.nom_raison_sociale}</div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
                        SIREN : {s.siren} — {s.siege?.code_postal} {s.siege?.libelle_commune}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: gridCols, gap: '12px' }}>
            {[
              { key: 'nom_societe', label: 'Nom société' },
              { key: 'siren', label: 'SIREN' },
              { key: 'nom_dirigeant', label: 'Dirigeant' },
              { key: 'adresse', label: 'Adresse' },
              { key: 'telephone', label: 'Téléphone' },
              { key: 'email', label: 'Email' },
              { key: 'canal', label: 'Canal' },
            ].map(({ key, label }) => (
              <div key={key}>
                <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: champObligatoire(key) ? '#ef4444' : '#6b7280' }}>
                  {label} {champObligatoire(key) ? '*' : ''}
                </label>
                <input value={form[key]} onChange={e => setForm({ ...form, [key]: key === 'telephone' ? formatPhone(e.target.value) : e.target.value })}
                  style={{ ...inputStyle, borderColor: champObligatoire(key) && !form[key] ? '#fca5a5' : '#e5e7eb' }} />
              </div>
            ))}
            <div>
              <label style={{ display: 'block', marginBottom: '4px', fontSize: '12px', fontWeight: '500', color: '#6b7280' }}>Type</label>
              <select value={form.type} onChange={e => setForm({ ...form, type: e.target.value })} style={inputStyle}>
                <option value="prospect">Prospect</option>
                <option value="client">Client</option>
                <option value="apporteur">Apporteur</option>
              </select>
            </div>
          </div>

          <div style={{ marginTop: '12px', fontSize: '12px', color: '#ef4444' }}>* Champs obligatoires</div>

          <div style={{ display: 'flex', gap: '10px', marginTop: '16px' }}>
            <button onClick={handleSubmit}
              style={{ background: '#1a2b4a', color: 'white', padding: '10px 24px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }}>
              Enregistrer
            </button>
            <button onClick={() => setShowForm(false)}
              style={{ background: 'white', color: '#6b7280', padding: '10px 24px', border: '1px solid #e5e7eb', borderRadius: '8px', cursor: 'pointer', fontSize: '14px' }}>
              Annuler
            </button>
          </div>
        </div>
      )}

      {vueMode === 'tableau' && (
      <div style={{ background: C.surface, borderRadius: RAYON.lg,
                    border: `1px solid ${C.bordure}`, overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: isMobile || isTablet ? '700px' : 'auto' }}>
          <thead>
            <tr style={{ background: C.fond, borderBottom: `1px solid ${C.bordure}` }}>
              {COLONNES.map(({ label, champ }) => {
                const actif = tri.colonne === champ;
                return (
                  <th key={champ} onClick={() => toggleTri(champ)}
                    style={{ padding: `${E.md} ${E.lg}`, textAlign: 'left', fontSize: '11px',
                             fontWeight: 700, color: actif ? C.texte : C.texteFaible,
                             textTransform: 'uppercase', letterSpacing: '0.07em',
                             cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>
                    {label}
                    <span style={{ marginLeft: '5px', opacity: actif ? 1 : 0.35 }}>
                      {actif ? (tri.direction === 'asc' ? '↑' : '↓') : '↕'}
                    </span>
                  </th>
                );
              })}
            </tr>
          </thead>
          {chargement ? <SqueletteTableau lignes={8} colonnes={COLONNES.length} /> : (
          <tbody>
            {prospectsAffiches.map((p, i) => {
              return (
              <tr key={p.id} onClick={() => navigate(`/prospects/${p.id}`)}
                style={{ borderBottom: `1px solid ${C.bordure}`, cursor: 'pointer', background: C.surface }}
                onMouseEnter={e => e.currentTarget.style.background = C.fond}
                onMouseLeave={e => e.currentTarget.style.background = C.surface}>
                <td style={{ padding: `${E.md} ${E.lg}`, fontSize: '13.5px', fontWeight: 600, color: C.texte }}>{p.nom_societe}</td>
                <td style={{ padding: `${E.md} ${E.lg}`, fontSize: '13.5px', color: C.texteMoyen }}>{p.nom_dirigeant}</td>
                <td style={{ padding: `${E.md} ${E.lg}`, fontSize: '13.5px', color: C.texteMoyen }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>{p.telephone}</span>
                    {p.telephone && (
                      <a href={`tel:${p.telephone.replace(/[\s.]/g, '')}`}
                        onClick={e => e.stopPropagation()} title="Appeler"
                        style={{ color: '#9ca3af', fontSize: '13px', textDecoration: 'none', lineHeight: 1, flexShrink: 0 }}>📞</a>
                    )}
                  </div>
                </td>
                <td style={{ padding: `${E.md} ${E.lg}`, fontSize: '13.5px', color: C.texteMoyen }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                    <span>{p.email}</span>
                    {p.email && (
                      <a href={`mailto:${p.email}`}
                        onClick={e => e.stopPropagation()} title="Envoyer un email"
                        style={{ color: '#9ca3af', fontSize: '13px', textDecoration: 'none', lineHeight: 1, flexShrink: 0 }}>✉️</a>
                    )}
                  </div>
                </td>
                <td style={{ padding: `${E.md} ${E.lg}` }}>
                  <span style={{
                    background: getTypeColor(p.type || 'prospect') + '1a',
                    color: getTypeColor(p.type || 'prospect'),
                    padding: '3px 10px', borderRadius: RAYON.rond, fontSize: '11.5px', fontWeight: 600,
                    whiteSpace: 'nowrap',
                  }}>
                    {getTypeLabel(p.type || 'prospect')}
                  </span>
                </td>
              </tr>
              );
            })}
          </tbody>
          )}
        </table>
        {!chargement && prospectsAffiches.length === 0 && (
          recherche || filtreType !== 'Tous' ? (
            <EtatVide
              titre="Aucune entreprise ne correspond"
              description="Essayez un autre terme de recherche, ou remettez le filtre sur tous les types."
              action={<button onClick={() => { setRecherche(''); setFiltreType('Tous'); }}
                              style={bouton('secondaire')}>Effacer les filtres</button>}
              compact />
          ) : (
            <EtatVide
              titre="Votre portefeuille est vide"
              description="Les entreprises que vous suivez — prospects, clients et apporteurs — apparaîtront ici. La recherche par SIREN remplit automatiquement la raison sociale, l'adresse et le dirigeant."
              action={<button onClick={() => setShowForm(true)} style={bouton('principal')}>
                        + Créer une première entreprise
                      </button>} />
          )
        )}
        </div>
        {totalPages > 1 && (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                gap: E.sm, padding: `${E.md} ${E.lg}`, borderTop: `1px solid ${C.bordure}`,
                flexWrap: 'wrap' }}>
    <span style={{ fontSize: '12.5px', color: C.texteFaible }}>
      Page {page} sur {totalPages} · {prospectsFiltres.length} entreprise(s)
    </span>
    <div style={{ display: 'flex', gap: E.xs, alignItems: 'center', flexWrap: 'wrap' }}>
      <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
        style={{ ...bouton('secondaire'), padding: '6px 12px',
                 opacity: page === 1 ? 0.45 : 1,
                 cursor: page === 1 ? 'not-allowed' : 'pointer' }}>
        Précédent
      </button>
      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
        <button key={p} onClick={() => setPage(p)}
          style={{ ...(page === p ? bouton('principal') : bouton('discret')),
                   padding: '6px 11px', minWidth: 32, justifyContent: 'center' }}>
          {p}
        </button>
      ))}
      <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
        style={{ ...bouton('secondaire'), padding: '6px 12px',
                 opacity: page === totalPages ? 0.45 : 1,
                 cursor: page === totalPages ? 'not-allowed' : 'pointer' }}>
        Suivant
      </button>
    </div>
  </div>
)}
      </div>
      )}
      {vueMode === 'carte' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {geocoding && geocodeTotal > 0 && (
            <div style={{ background: 'white', borderRadius: '8px', padding: '12px 16px', border: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px', fontSize: '13px', color: '#6b7280' }}>
                <span>Géocodage des adresses via OpenStreetMap…</span>
                <span>{Math.round((geocodeProgress / Math.max(geocodeTotal, 1)) * 100)}%</span>
              </div>
              <div style={{ background: '#e5e7eb', borderRadius: '4px', height: '6px' }}>
                <div style={{ background: '#1a2b4a', borderRadius: '4px', height: '6px', width: `${Math.round((geocodeProgress / Math.max(geocodeTotal, 1)) * 100)}%`, transition: 'width 0.3s' }} />
              </div>
            </div>
          )}
          <div style={{ fontSize: '13px', color: '#6b7280' }}>
            {displayedGeoMarkers.length} entreprise(s) géolocalisée(s) affichée(s)
          </div>
          <div style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', height: 'calc(100vh - 300px)', minHeight: '450px' }}>
            <MapContainer center={[46.5, 2.5]} zoom={6} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClusterLayer markers={displayedGeoMarkers} navigate={navigate} />
            </MapContainer>
          </div>
        </div>
      )}
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Modal Nouvelle affaire chaude */}
      {showAcModal && (
        <div onClick={() => setShowAcModal(false)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px' }}>
          <div onClick={e => e.stopPropagation()} style={{ background: 'white', borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#1a2b4a', fontSize: '18px', fontWeight: '700' }}>Nouvelle affaire chaude</h2>
              <button onClick={() => setShowAcModal(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#9ca3af' }}>×</button>
            </div>

            {/* Choix du module — en tête, parce qu'il commande les champs qui
                suivent et la table dans laquelle l'affaire atterrit. */}
            <div style={{ marginBottom: '16px' }}>
              <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '6px' }}>Type d'affaire *</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { v: 'credit',    l: '💰 Crédit',    precision: 'projet de financement' },
                  { v: 'assurance', l: '🛡️ Assurance', precision: "contrat d'assurance" },
                  { v: 'vehicule',  l: '🚗 Véhicule',  precision: 'financement et assurance' },
                ].map(({ v, l, precision }) => (
                  <button key={v} onClick={() => setAcForm(f => ({ ...f, module: v }))}
                    style={{
                      flex: 1, padding: '10px 12px', borderRadius: '8px', cursor: 'pointer',
                      border: `2px solid ${acForm.module === v ? C.danger : '#e5e7eb'}`,
                      background: acForm.module === v ? '#fff5f5' : 'white',
                      textAlign: 'left', fontFamily: 'inherit',
                    }}>
                    <div style={{ fontSize: '13px', fontWeight: acForm.module === v ? '700' : '500', color: '#1a2b4a' }}>{l}</div>
                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>{precision}</div>
                  </button>
                ))}
              </div>
            </div>

            {/* SIREN search */}
            <div style={{ background: '#f0f7ff', padding: '14px', borderRadius: '8px', marginBottom: '16px', border: '1px solid #bfdbfe' }} ref={acSearchRef}>
              <p style={{ margin: '0 0 8px', fontSize: '13px', fontWeight: '600', color: '#1a2b4a' }}>Recherche automatique (nom ou SIREN)</p>
              <div style={{ position: 'relative' }}>
                <input
                  placeholder="Tapez le nom ou le SIREN de l'entreprise..."
                  value={acSearchQuery}
                  onChange={e => setAcSearchQuery(e.target.value)}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', background: 'white', width: '100%', boxSizing: 'border-box', paddingRight: acSearchLoading ? '40px' : '14px' }}
                />
                {acSearchLoading && <span style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', fontSize: '12px' }}>...</span>}
                {acShowSuggestions && acSuggestions.length > 0 && (
                  <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000, background: 'white', borderRadius: '8px', boxShadow: '0 8px 24px rgba(0,0,0,0.12)', border: '1px solid #e5e7eb', maxHeight: '240px', overflowY: 'auto', marginTop: '4px' }}>
                    {acSuggestions.map((s, i) => (
                      <div key={i} onClick={() => acSelectEntreprise(s)}
                        style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: '1px solid #f3f4f6' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                        onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                        <div style={{ fontWeight: '600', color: '#1a2b4a', fontSize: '13px' }}>{s.nom_raison_sociale}</div>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>SIREN : {s.siren} — {s.siege?.code_postal} {s.siege?.libelle_commune}</div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Champs */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Nom de l'entreprise *</label>
                <input value={acForm.nom_societe} onChange={e => setAcForm(f => ({ ...f, nom_societe: e.target.value }))}
                  placeholder="Nom de la société"
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
              {acForm.module === 'vehicule' ? (
                <>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Véhicule *</label>
                    <input value={acForm.designation} onChange={e => setAcForm(f => ({ ...f, designation: e.target.value }))}
                      placeholder="Ex : Renault Master 2024"
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                  </div>
                  {/* Les deux volets de l'affaire, cochés par défaut : c'est le
                      cas courant. Chacun ouvre ses propres champs. */}
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {[
                      { cle: 'avec_financement', libelle: '💰 Financement' },
                      { cle: 'avec_assurance',   libelle: '🛡️ Assurance' },
                    ].map(v => (
                      <label key={v.cle} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '600', color: '#374151', cursor: 'pointer' }}>
                        <input type="checkbox" checked={acForm[v.cle]} onChange={e => setAcForm(f => ({ ...f, [v.cle]: e.target.checked }))} />
                        {v.libelle}
                      </label>
                    ))}
                  </div>
                  {acForm.avec_financement && (
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Valeur du véhicule (€)</label>
                      <input type="number" min="0" value={acForm.valeur_bien} onChange={e => setAcForm(f => ({ ...f, valeur_bien: e.target.value }))}
                        placeholder="Ex : 42000"
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  )}
                  {acForm.avec_assurance && (
                    <div>
                      <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Prime annuelle (€)</label>
                      <input type="number" min="0" step="0.01" value={acForm.prime_annuelle} onChange={e => setAcForm(f => ({ ...f, prime_annuelle: e.target.value }))}
                        placeholder="Ex : 1800"
                        style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                    </div>
                  )}
                </>
              ) : acForm.module === 'assurance' ? (
                <>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Type de contrat *</label>
                    <select value={acForm.type_contrat} onChange={e => setAcForm(f => ({ ...f, type_contrat: e.target.value }))}
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box', background: 'white', fontFamily: 'inherit' }}>
                      {Object.entries(TYPE_CONTRAT_LABELS).map(([code, libelle]) => (
                        <option key={code} value={code}>{libelle}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Prime annuelle (€)</label>
                    <input type="number" min="0" step="0.01" value={acForm.prime_annuelle} onChange={e => setAcForm(f => ({ ...f, prime_annuelle: e.target.value }))}
                      placeholder="Ex : 4500"
                      style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                  </div>
                </>
              ) : (
                <div>
                  <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Valeur du bien (€)</label>
                  <input type="number" min="0" value={acForm.valeur_bien} onChange={e => setAcForm(f => ({ ...f, valeur_bien: e.target.value }))}
                    placeholder="Ex : 150000"
                    style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
                </div>
              )}
              <div>
                <label style={{ fontSize: '13px', fontWeight: '600', color: '#374151', display: 'block', marginBottom: '4px' }}>Date d'échéance *</label>
                <input type="date" value={acForm.date_echeance} onChange={e => setAcForm(f => ({ ...f, date_echeance: e.target.value }))}
                  style={{ padding: '10px 14px', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '14px', outline: 'none', width: '100%', boxSizing: 'border-box' }} />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
              <button onClick={() => setShowAcModal(false)}
                style={{ flex: 1, padding: '11px', borderRadius: '8px', border: '1px solid #e5e7eb', background: 'white', color: '#374151', fontSize: '14px', cursor: 'pointer', fontWeight: '500' }}>
                Annuler
              </button>
              <button onClick={handleAffaireChaudeSubmit} disabled={acSubmitting}
                style={{ flex: 2, padding: '11px', borderRadius: '8px', border: 'none', background: acSubmitting ? '#fca5a5' : '#ef4444', color: 'white', fontSize: '14px', cursor: acSubmitting ? 'not-allowed' : 'pointer', fontWeight: '600' }}>
                {acSubmitting ? 'Création...'
                  : `🔥 Créer l'affaire chaude ${acForm.module === 'assurance' ? 'assurance' : acForm.module === 'vehicule' ? 'véhicule' : 'crédit'}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Prospects;